import { useEffect, useRef, useState, useCallback } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, X, Store, HardHat, LogIn, Search, User as UserIcon } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import { useAuth } from "@/context/AuthContext";

const TRADE_LINKS = [
  { label: "Sell flooring with FloorMatch", to: "/sell", icon: Store, testId: "trade-sell" },
  { label: "Join as a fitter or delivery partner", to: "/fitters", icon: HardHat, testId: "trade-fitters" },
  { label: "Retailer sign-in", to: "/retailer/login", icon: LogIn, testId: "trade-retailer-login" },
];

export default function PublicHeader() {
  const [tradeOpen, setTradeOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const tradeRef = useRef(null);
  const tradeBtnRef = useRef(null);
  const menuBtnRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCustomerish = user && user.role === "customer";

  // Navigate to a route, handling the /#how-it-works hash anchor.
  const goTo = useCallback(
    (to) => {
      setMenuOpen(false);
      setTradeOpen(false);
      if (to.startsWith("/#")) {
        const id = to.slice(2);
        if (location.pathname === "/") {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        } else {
          navigate("/" + to.slice(1)); // ScrollManager handles the scroll
        }
        return;
      }
      navigate(to);
    },
    [location.pathname, navigate],
  );

  // Desktop dropdown: close on outside click + Escape.
  useEffect(() => {
    if (!tradeOpen) return;
    const onDown = (e) => {
      if (tradeRef.current && !tradeRef.current.contains(e.target)) setTradeOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setTradeOpen(false);
        tradeBtnRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [tradeOpen]);

  // Mobile menu: lock body scroll + Escape to close.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        menuBtnRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const navLinkCls = ({ isActive }) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors hover:text-[#E05915] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E05915] ${
      isActive ? "text-[#E05915]" : "text-slate-700"
    }`;

  return (
    <>
      <header
        className="sticky top-0 z-40 backdrop-blur-xl bg-white/90 border-b border-slate-200"
        data-testid="public-header"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 md:h-[96px] flex items-center justify-between gap-2">
          <Link to="/" aria-label="FloorMatch home" className="flex items-center gap-2.5 md:gap-3 group shrink-0 min-w-0" data-testid="brand-link">
            <BrandMark className="h-11 w-11 md:h-[74px] md:w-[74px] shrink-0 transition-transform group-hover:-translate-y-0.5" alt="FloorMatch" />
            <span className="flex flex-col justify-center leading-none min-w-0">
              <span className="font-display font-extrabold tracking-tight text-[#0F3D2E] text-base md:hidden truncate">
                Floor<span className="text-[#E05915]">Match</span>
              </span>
              <span className="hidden md:inline font-display font-extrabold tracking-tight text-[#0F3D2E] text-2xl">
                FLOOR<span className="text-[#E05915]">MATCH</span>
              </span>
              <span className="hidden md:inline text-[11px] text-slate-500 mt-1">The perfect fit. Near you.</span>
            </span>
          </Link>

          {/* Desktop navigation */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Primary">
            <NavLink to="/search" className={navLinkCls} data-testid="nav-browse">
              Browse
            </NavLink>
            <button
              type="button"
              onClick={() => goTo("/#how-it-works")}
              className="px-3 py-2 rounded-md text-sm font-medium text-slate-700 hover:text-[#E05915] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E05915]"
              data-testid="nav-how-it-works"
            >
              How it works
            </button>
            <NavLink to="/sustainability" className={navLinkCls} data-testid="nav-sustainability">
              Sustainability
            </NavLink>

            {/* Trade partners dropdown */}
            <div className="relative" ref={tradeRef}>
              <button
                ref={tradeBtnRef}
                type="button"
                onClick={() => setTradeOpen((o) => !o)}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-slate-700 hover:text-[#2A5A3B] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2A5A3B]"
                aria-haspopup="menu"
                aria-expanded={tradeOpen}
                aria-controls="trade-menu"
                data-testid="nav-trade-partners"
              >
                Trade partners
                <ChevronDown className={`w-4 h-4 transition-transform ${tradeOpen ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>
              {tradeOpen && (
                <div
                  id="trade-menu"
                  role="menu"
                  aria-label="Trade partners"
                  className="absolute right-0 top-full mt-2 w-72 rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 p-2 z-50"
                  data-testid="trade-dropdown"
                >
                  {TRADE_LINKS.map((t) => (
                    <button
                      key={t.to}
                      role="menuitem"
                      onClick={() => goTo(t.to)}
                      className="w-full flex items-center gap-3 text-left px-3 py-2.5 rounded-xl text-sm text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50 focus-visible:ring-2 focus-visible:ring-[#2A5A3B] transition-colors"
                      data-testid={t.testId}
                    >
                      <span className="grid place-items-center w-8 h-8 rounded-lg bg-[#2A5A3B]/10 text-[#2A5A3B] shrink-0">
                        <t.icon className="w-4 h-4" aria-hidden="true" />
                      </span>
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Desktop right actions */}
          <div className="hidden md:flex items-center gap-3 shrink-0">
            {user ? (
              <Link
                to="/account"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-[#0F3D2E] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded px-1"
                data-testid="nav-account"
              >
                <UserIcon className="w-4 h-4" aria-hidden="true" />
                {isCustomerish ? "My account" : "Account"}
              </Link>
            ) : (
              <Link
                to="/account"
                className="text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded px-1"
                data-testid="nav-signin"
              >
                Sign in
              </Link>
            )}
            <Link
              to="/search"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#E05915] text-white text-sm font-semibold hover:bg-[#c24b10] transition-colors shadow-sm shadow-[#E05915]/30"
              data-testid="nav-find-flooring"
            >
              <Search className="w-4 h-4" aria-hidden="true" /> Find flooring
            </Link>
          </div>

          {/* Mobile right actions */}
          <div className="flex md:hidden items-center gap-2 shrink-0">
            <Link
              to="/search"
              className="inline-flex items-center gap-1 h-11 px-3.5 rounded-full bg-[#E05915] text-white text-sm font-semibold hover:bg-[#c24b10] transition-colors"
              data-testid="nav-find-flooring-mobile"
            >
              <Search className="w-4 h-4" aria-hidden="true" /> Find
            </Link>
            <button
              ref={menuBtnRef}
              type="button"
              onClick={() => setMenuOpen(true)}
              className="grid place-items-center w-11 h-11 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E05915]"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {menuOpen && (
        <MobileMenu onClose={() => setMenuOpen(false)} goTo={goTo} returnFocus={menuBtnRef} user={user} />
      )}
    </>
  );
}

function MobileMenu({ onClose, goTo, returnFocus, user }) {
  const firstRef = useRef(null);
  useEffect(() => {
    firstRef.current?.focus();
  }, []);

  const CUSTOMER = [
    { label: "Find flooring", to: "/search", testId: "m-find-flooring", primary: true },
    { label: "Browse", to: "/search", testId: "m-browse" },
    { label: "Floor several rooms", to: "/whole-home", testId: "m-whole-home" },
    { label: "How it works", to: "/#how-it-works", testId: "m-how-it-works" },
    { label: "Sustainability", to: "/sustainability", testId: "m-sustainability" },
    user
      ? { label: "My account", to: "/account", testId: "m-account", muted: true }
      : { label: "Sign in", to: "/account", testId: "m-signin", muted: true },
  ];

  return (
    <div
      className="fixed inset-0 z-50 bg-white md:hidden flex flex-col"
      id="mobile-menu"
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
      data-testid="mobile-menu"
    >
      <div className="h-16 px-4 flex items-center justify-between border-b border-slate-200 shrink-0">
        <BrandMark className="w-12 h-12" />
        <button
          type="button"
          onClick={() => {
            onClose();
            returnFocus?.current?.focus();
          }}
          className="grid place-items-center w-10 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E05915]"
          aria-label="Close menu"
          data-testid="mobile-menu-close"
        >
          <X className="w-6 h-6" aria-hidden="true" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400 px-1 mb-2">Customer</p>
        <div className="space-y-2">
          {CUSTOMER.map((item, i) => (
            <button
              key={item.testId}
              ref={i === 0 ? firstRef : null}
              onClick={() => goTo(item.to)}
              className={`w-full text-left px-4 rounded-xl font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E05915] ${
                item.primary
                  ? "h-14 bg-[#E05915] text-white text-base hover:bg-[#c24b10] flex items-center gap-2"
                  : item.muted
                    ? "min-h-[52px] py-3.5 text-slate-500 text-base hover:bg-slate-50 flex items-center"
                    : "min-h-[52px] py-3.5 text-slate-800 text-base hover:bg-slate-50 flex items-center"
              }`}
              data-testid={item.testId}
            >
              {item.primary && <Search className="w-5 h-5" aria-hidden="true" />}
              {item.label}
            </button>
          ))}
        </div>

        <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-200 p-3">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 px-1 mb-2">Trade partners</p>
          <div className="space-y-1">
            {TRADE_LINKS.map((t) => (
              <button
                key={t.to}
                onClick={() => goTo(t.to)}
                className="w-full flex items-center gap-3 text-left px-3 py-3.5 rounded-xl text-base text-slate-700 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2A5A3B] transition-colors"
                data-testid={`m-${t.testId}`}
              >
                <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#2A5A3B]/10 text-[#2A5A3B] shrink-0">
                  <t.icon className="w-4 h-4" aria-hidden="true" />
                </span>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
